import React, { useEffect, useState, useContext } from 'react';
import DashboardOnboarding from './DashboardOnboarding';
import DashboardNextStepDialog, { PendingStep } from './DashboardNextStepDialog';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardProps,
  Container,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  styled,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { useAppDispatch } from 'store/hooks';
import { getProfileStatus, getPatientDetails, getPendingOnboardingStep } from 'store/slices/userSlice';
import {
  getMCATStatus,
  getMFSHLink,
  getMFSHStatus,
  getWellbeingStatus,
  getMCATScore,
  getWellbeingScore,
} from 'store/slices/mcatSlice';

import { AuthContext, useAuth } from 'utils/AuthContext';
import { parseAppointmentUtc } from 'utils/dataFormatter';
import DashboardFooter from './DashboardFooter';
import { ROUTE } from 'route/RouteEnums';
import AppointmentStatus from './AppointmentStatus';
import ConsentDocumentStatus from './ConsentDocumentStatus';
import { toast } from 'react-toastify';
import { getAppointmentConfirmationDetails } from 'store/slices/appointmentSlice';
import { StyledDialog } from 'theme/index';
import { checkDocumentStatus } from 'store/slices/documentSlice';
import DashboardGreeting from 'components/SiblingSwitch/DashboardGreeting';
import { usePatientSelectionPending } from 'components/SiblingSwitch/patientSelection';
import DashboardTile from './DashboardTile';

const ACTIONABLE_PENDING_STEPS: PendingStep[] = ['PROFILE', 'CONSENT', 'MFSH', 'MWISE'];

const isActionablePendingStep = (step: string | null | undefined): step is PendingStep =>
  !!step && ACTIONABLE_PENDING_STEPS.includes(step as PendingStep);

type ProfileStatus = {
  patientInfo: boolean;
  guardianInfo: boolean;
  providerInfo: boolean;
  counselorInfo: boolean;
  pharmacyDetails: boolean;
  insuranceDetails: boolean;
};
const Dashboard = (props: any) => {
  const StyleCard = styled(Card)<CardProps>(({ theme }) => ({
    boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
    border: '1px solid #EFF4FF',
    borderRadius: 12,
    background: '#FFF',
    margin: '12px 16px',
    position: 'relative',
    zIndex: 99,
    '& .MuiTypography-root': {
      marginBottom: '8px',
      position: 'relative',
    },
  }));
  const [isOpen, setIsOpen] = useState(sessionStorage.getItem('firstimelogin') === 'yes');
  const [profileStatus, setProfileStatus] = useState<string>('incomplete');
  const [mcatStatus, setmcatStatus] = useState<string>('');
  const [displayModal] = useState<boolean>(false);
  const [wellbeingStatus, setWellbeingStatus] = useState<any>();
  const [mfshStatus, setMfshStatus] = useState<string>('incomplete');
  const [mcatScore, setMcatScore] = useState();
  const [wellbeingScore, setWellbeingScore] = useState();
  const [wellbeingScoreCategory, setWellbeingScoreCategory] = useState<string>();
  const [mcatScoreCategory, setMcatScoreCategory] = useState<string>();
  const [trackYourProgress, setTrackYourProgress] = useState<boolean>(false);
  const [migrationStatus, setMigrationStatus] = useState<boolean>(false);
  const [inActiveAppointment, setInActiveAppointment] = useState<boolean>(false);
  const [hasMoreAppointment, setHasMoreAppointment] = useState<boolean>(false);
  const [isDatePass, setIsDatePass] = useState<boolean>(false);

  const navigate = useNavigate();
  const { user, setUserDetails } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<ProfileStatus | null>(null);
  const [clinicalDocument, setClinicalDocument] = useState<string>('');
  const [pendingStep, setPendingStep] = useState<PendingStep | null>(null);
  const [showNextStepDialog, setShowNextStepDialog] = useState(false);
  /** MM-1172: patient picker is up; the onboarding and next-step dialogs wait until a child is chosen. */
  const selectingPatient = usePatientSelectionPending();

  useEffect(() => {
    if (!user?.patientId) return;
    dispatch(checkDocumentStatus(user.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setClinicalDocument(response?.data.status);
        }
      });
  }, [dispatch, user?.patientId]);

  const getPendingOnboardingStepData = () => {
    if (!user?.patientId) return;
    dispatch(getPendingOnboardingStep(user.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const step = response?.data?.pendingStep as string | null;
          if (isActionablePendingStep(step)) {
            setPendingStep(step);
          } else {
            setPendingStep(null);
            setShowNextStepDialog(false);
          }
        }
      })
      .catch(() => {
        setPendingStep(null);
        setShowNextStepDialog(false);
      });
  };
  const getProfileStatusData = () => {
    dispatch(getProfileStatus(user?.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const { data } = response;
          const isProfileCompleted =
            data?.patientInfoStatus &&
            data?.guardianInfoStatus &&
            data?.childProviderInfoStatus &&
            data?.childProviderInfoStatus &&
            data?.pharmacyInfoStatus &&
            data?.paymentInfoStatus;

          setStatus({
            patientInfo: data?.patientInfoStatus,
            guardianInfo: data?.guardianInfoStatus,
            providerInfo: data?.childProviderInfoStatus,
            counselorInfo: data?.childCounselorInfoStatus,
            pharmacyDetails: data?.pharmacyInfoStatus,
            insuranceDetails: data?.paymentInfoStatus,
          });
          if (isProfileCompleted) {
            setProfileStatus('COMPLETED');
            const data = {
              ...user,
              profileStatus: 'COMPLETED',
            };
            setUserDetails(data);
          } else {
            setProfileStatus('incomplete');
            const data = {
              ...user,
              profileStatus: 'incomplete',
            };
            setUserDetails(data);
          }
        }
      });
  };

  const getMCATStatusData = () => {
    dispatch(getMCATStatus(user?.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setmcatStatus(response?.data);
        }
      });
  };

  const getWellbeingStatusData = () => {
    dispatch(getWellbeingStatus(user?.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setWellbeingStatus(response?.data);
          setTrackYourProgress(response?.data?.trackYourProgress);
        }
      });
  };

  const getMFSHStatusData = () => {
    dispatch(getMFSHStatus(user?.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setMfshStatus(response?.data);
        }
      });
  };
  const getPatientDetailsData = () => {
    dispatch(getPatientDetails(user?.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          if (response.data.migrationStatus === 'MG-A' || response.data.migrationStatus === 'MG-R') {
            setMigrationStatus(true);
          }
        }
      });
  };
  const getMCATScoreData = () => {
    dispatch(getMCATScore(user?.patientId))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          setMcatScore(response?.data?.mcatIScore);
          const mcatScore = response?.data?.mcatIScore;
          if (mcatScore < 50) {
            setMcatScoreCategory('In Crisis');
          } else if (mcatScore > 50 && mcatScore < 100) {
            setMcatScoreCategory('Critical');
          } else if (mcatScore > 101 && mcatScore < 225) {
            setMcatScoreCategory('Struggling');
          } else if (mcatScore > 226 && mcatScore < 325) {
            setMcatScoreCategory('Vulnerable');
          } else if (mcatScore > 326 && mcatScore < 400) {
            setMcatScoreCategory('Balanced');
          } else if (mcatScore > 401 && mcatScore < 450) {
            setMcatScoreCategory('Stable');
          } else if (mcatScore > 451 && mcatScore < 500) {
            setMcatScoreCategory('Thriving');
          }
        }
      });
  };

  const getWellbeingScoreData = () => {
    dispatch(getWellbeingScore(user?.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const wellbeingScore = response?.data?.wellnessScore;
          setWellbeingScore(wellbeingScore);
          if (wellbeingScore < 100) {
            setWellbeingScoreCategory('In Crisis');
          } else if (wellbeingScore > 100 && wellbeingScore < 200) {
            setWellbeingScoreCategory('Critical');
          } else if (wellbeingScore > 200 && wellbeingScore < 500) {
            setWellbeingScoreCategory('Struggling');
          } else if (wellbeingScore > 500 && wellbeingScore < 600) {
            setWellbeingScoreCategory('Vulnerable');
          } else if (wellbeingScore > 600 && wellbeingScore < 800) {
            setWellbeingScoreCategory('Balanced');
          } else if (wellbeingScore > 800 && wellbeingScore < 900) {
            setWellbeingScoreCategory('Stable');
          } else if (wellbeingScore > 900 && wellbeingScore < 1000) {
            setWellbeingScoreCategory('Thriving');
          }
        }
      });
  };

  const getAppointmentConfirmationDetailsData = () => {
    dispatch(getAppointmentConfirmationDetails(user?.patientId))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          const drChronoStatus = response?.data?.status;
          const isFinalStatus = ['No-Show', 'Cancelled', 'Complete', 'Late Cancel'].includes(drChronoStatus);
          // MM-1207: the API value is UTC without a zone; new Date() read it as browser-local time.
          // No appointment still counts as passed, as before (new Date(null) was 1970).
          const appointmentUtc = parseAppointmentUtc(response?.data?.upcomingAppointmentDateTime);
          const isPastDate = !appointmentUtc || appointmentUtc.valueOf() <= Date.now();
          setIsDatePass(isPastDate);
          setHasMoreAppointment(response?.data?.hasMoreAppointment);
          setInActiveAppointment(isFinalStatus);
        }
      });
  };

  useEffect(() => {
    window.onpopstate = () => {
      navigate(ROUTE.DASHBOARD);
    };
    getProfileStatusData();
    getMCATStatusData();
    getWellbeingStatusData();
    getMFSHStatusData();
    getPatientDetailsData();
    getMCATScoreData();
    getWellbeingScoreData();
    getAppointmentConfirmationDetailsData();
    getPendingOnboardingStepData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user.userId, user?.patientId, profileStatus]);

  useEffect(() => {
    if (pendingStep && !isOpen && !selectingPatient) {
      setShowNextStepDialog(true);
    }
  }, [pendingStep, isOpen, selectingPatient]);

  const handleModalClose = () => {
    setIsOpen(false);
    sessionStorage.removeItem('firstimelogin');
  };

  const handleConsentClick = () => {
    if (!user?.patientId) return;
    if (profileStatus !== 'COMPLETED') {
      toast('Please fill the profile info first', {
        type: 'info',
        icon: true,
      });
      return;
    }
    dispatch(checkDocumentStatus(user.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const documentStatus = response?.data;
          if (documentStatus?.source?.toUpperCase() === 'BOLDSIGN') {
            navigate(ROUTE.OPEN_POLICY_DOCUMENTS, { state: { signLink: documentStatus.signLink } });
          } else if (documentStatus?.signLink) {
            window.open(documentStatus.signLink, '_blank');
          }
        }
      });
  };

  const handleNextStepDialogClose = () => {
    setShowNextStepDialog(false);
  };

  const handleStartTest = () => {
    if (migrationStatus === false && mfshStatus !== 'COMPLETED') {
      toast('Please complete medical & family history to proceed with this step.', {
        type: 'info',
        icon: true,
      });
    } else if (mcatStatus === 'PENDING') {
      navigate(ROUTE.MCAT_ASSESSMENT);
    } else {
      navigate(ROUTE.MCAT_TEST);
    }
  };

  const handleWellbeing = (val: string) => {
    if (mcatStatus !== 'COMPLETED' && migrationStatus === false) {
      toast('Please complete M-Wise to proceed with this step.', {
        type: 'info',
        icon: true,
      });
    } else if (migrationStatus && clinicalDocument !== 'Completed') {
      toast('Please review and complete the Policies & Consents before proceeding with this step', {
        type: 'info',
        icon: true,
      });
    } else {
      navigate(`${ROUTE.WELLBEING_ASSESSMENT}`, { state: { wellbeingUser: val } });
    }
  };

  const handleWellnessDetails = () => {
    navigate(`${ROUTE.WELLNESS_DASHBOARD}`);
  };

  const handleMCATDetails = () => {
    navigate(`${ROUTE.MCAT_DASHBOARD}`);
  };

  const handleViewDetails = () => {
    navigate(`${ROUTE.TREATMENT}`);
  };

  const handleJotForm = () => {
    if (profileStatus !== 'COMPLETED') {
      toast('Please fill the profile info first', {
        type: 'info',
        icon: true,
      });
    } else if (clinicalDocument !== 'Completed') {
      toast('Please review and complete the Policies & Consents before proceeding with this step.', {
        type: 'info',
        icon: true,
      });
    } else if (startMFSH) {
      dispatch(getMFSHLink(user?.patientId))
        .unwrap()
        .then((response: any) => {
          if (response?.status === 200) {
            const source = response?.data.source;
            if (source === 'In-App') {
              if (mfshStatus === 'PENDING') {
                navigate(ROUTE.MFSH_ASSESSMENT);
              } else {
                navigate(ROUTE.MFSH_NAVIGATE);
              }
            } else if (source === 'Jotform') {
              const windowReference = window.open();
              if (windowReference) {
                windowReference.location = `//${response?.data?.url}`;
              }
            }
          }
        });
    }
  };

  const handleProfileClick = () => {
    if (!status) return;
    if (!status.patientInfo) {
      navigate(ROUTE.PATIENT_INFORMATION);
    } else if (!status.guardianInfo) {
      navigate(ROUTE.GUARDIAN_INFORMATION);
    } else if (!status.providerInfo) {
      navigate(ROUTE.PROVIDER_INFORMATION);
    } else if (!status.counselorInfo) {
      navigate(ROUTE.COUNSELOR_INFORMATION);
    } else if (!status.pharmacyDetails) {
      navigate(ROUTE.PHARMACY_DETAILS);
    } else {
      navigate(ROUTE.INSURANCE_DETAILS);
    }
  };

  const handlePrimaryNextStepAction = () => {
    if (!pendingStep) return;
    switch (pendingStep) {
      case 'PROFILE':
        handleProfileClick();
        break;
      case 'CONSENT':
        handleConsentClick();
        break;
      case 'MFSH':
        handleJotForm();
        break;
      case 'MWISE':
        handleStartTest();
        break;
      default:
        break;
    }
    setShowNextStepDialog(false);
  };

  const isFullyCompleted =
    migrationStatus === false &&
    profileStatus === 'COMPLETED' &&
    clinicalDocument === 'Completed' &&
    mfshStatus === 'COMPLETED';

  const isMigrationNew =
    migrationStatus === true &&
    profileStatus === 'COMPLETED' &&
    clinicalDocument === 'Completed' &&
    mfshStatus === 'NEW';

  const shouldShowFullOpacity1 = (isFullyCompleted && mcatStatus === 'COMPLETED') || isMigrationNew;
  const opacityBasedOnTest = shouldShowFullOpacity1 ? 1 : 0.5;
  const startMFSH =
    !migrationStatus && profileStatus === 'COMPLETED' && mfshStatus !== 'COMPLETED' && clinicalDocument === 'Completed';

  const isOpacityTest2Completed =
    migrationStatus === false &&
    profileStatus === 'COMPLETED' &&
    clinicalDocument === 'Completed' &&
    mfshStatus === 'COMPLETED';

  const isOpacityTest2MigrationNew =
    profileStatus === 'COMPLETED' &&
    clinicalDocument === 'Completed' &&
    migrationStatus !== false &&
    mfshStatus === 'NEW';

  const shouldShowFullOpacity = isOpacityTest2Completed || isOpacityTest2MigrationNew;
  const opacityBasedOnTest2 = shouldShowFullOpacity ? 1 : 0.5;

  /** Wellbeing survey statuses that still need the survey to be filled in. */
  const SURVEY_DUE_STATUSES = ['NEW', 'PENDING'];
  /** Whether the parent wellbeing survey is still to be completed (shows its button). */
  const isParentSurveyDue = SURVEY_DUE_STATUSES.includes(wellbeingStatus?.parentStatus);
  /** Whether the child wellbeing survey is still to be completed (shows its button). */
  const isChildSurveyDue = SURVEY_DUE_STATUSES.includes(wellbeingStatus?.childStatus);

  return (
    <>
      <DashboardOnboarding isOpen={isOpen && !selectingPatient} handleModalClose={handleModalClose} />
      {pendingStep ? (
        <DashboardNextStepDialog
          open={showNextStepDialog}
          pendingStep={pendingStep}
          onPrimaryClick={handlePrimaryNextStepAction}
          onClose={handleNextStepDialogClose}
        />
      ) : null}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Container component='section' maxWidth='md'>
            <Typography
              variant='h5'
              position={'relative'}
              width={'fit-content'}
              sx={{ paddingTop: 6 }}
            >
              {/* MM-1123: "<child>'s Dashboard", underline beneath the child's name. No capitalize style:
                  the name is already capitalised and it would turn "'s" into "'S". */}
              <DashboardGreeting />
            </Typography>
            <Grid container marginTop={4}>
              {/* --------------Profile Setup--------------- */}
              {/* Full-width tile, same layout as the MFSH tile: icon left, information centre,
                  button right. */}
              {profileStatus === 'incomplete' && (
                <Grid item xs={12}>
                  <StyleCard>
                    <CardContent>
                      <Grid container spacing={1} alignItems={'center'}>
                        <Grid item xs={12} md={2}>
                          
                          <CardMedia
                            component={'img'}
                            src='images/profile.png'
                            alt='complete profile'
                            sx={{ width: '97px', height: 'auto' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant='h2'>Complete Profile</Typography>
                          <Typography variant='body1'>
                            Provide the patient&apos;s demographic, guardian, PCP, Insurance information to complete the
                            profile.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4} textAlign={'end'}>
                          <Button variant='outlined' onClick={handleProfileClick}>
                            Let&apos;s Start
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </StyleCard>
                </Grid>
              )}
              {/* -------------- Appointment (Clinical Visit) ------------- */}
              {/* Full width on its own row, like the MFSH tile - no longer shares a row with Profile. */}
              <Grid item xs={12} sx={{ opacity: profileStatus === 'incomplete' ? 0.5 : 1 }}>
                <AppointmentStatus profileStatus={profileStatus} />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  opacity: profileStatus === 'COMPLETED' ? 1 : 0.5,
                }}
              >
                <ConsentDocumentStatus profileStatus={profileStatus} />
              </Grid>

              {/* -------------------3rd row ------------------------------- */}
              {/* ------------------ MFSH -------------- */}
              {mfshStatus !== 'COMPLETED' && migrationStatus === false ? (
                <Grid
                  item
                  xs={12}
                  sx={{
                    opacity: profileStatus === 'COMPLETED' && clinicalDocument === 'Completed' ? 1 : 0.5,
                  }}
                >
                  <StyleCard>
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={2}>
                          <CardMedia
                            component={'img'}
                            src='images/Family_Icon.png'
                            alt='card-img'
                            sx={{ width: '97px', height: 'auto' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} mt={'10px'}>
                          <Typography variant='h2'>Complete Medical & Family Social Hx</Typography>
                          <Typography variant='body1'>
                            Help us understand your child&apos;s background by providing their medical, family, and social history.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4} mt={'25px'} textAlign={'end'}>
                          <Button variant='outlined' onClick={handleJotForm}>
                            {mfshStatus === 'PENDING' ? 'Resume' : 'Start'}
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </StyleCard>
                </Grid>
              ) : null}

              {/* MM-1123: every widget below is a full-width DashboardTile, same layout as Profile / Consent */}
              {/* --------------- MCAT (M-Wise) -------------- */}
              {mcatStatus !== 'COMPLETED' && migrationStatus === false ? (
                <Grid item xs={12} sx={{ opacity: opacityBasedOnTest2 }}>
                  <DashboardTile
                    icon='images/Brain_icon.png'
                    iconAlt='complete m-wise'
                    title='Complete M-Wise'
                    description={
                      'Help your provider understand your child’s mental health by completing the comprehensive ' +
                      'M-Wise assessment.'
                    }
                    actions={
                      <Button variant='outlined' onClick={handleStartTest}>
                        Start Test
                      </Button>
                    }
                  />
                </Grid>
              ) : null}
              {/* --------- Wellbeing (Track Your Progress) ----------------*/}
              {!(isDatePass ?? inActiveAppointment) && trackYourProgress && (isParentSurveyDue || isChildSurveyDue) && (
                <Grid item xs={12} sx={{ opacity: opacityBasedOnTest }}>
                  <DashboardTile
                    icon='images/Group 9505.png'
                    iconAlt='track your progress'
                    title='Track Your Progress'
                    description={
                      'Complete a brief questionnaire to monitor your journey and assess your overall wellness.'
                    }
                    actions={
                      <>
                        {isParentSurveyDue ? (
                          <Button variant='outlined' onClick={() => handleWellbeing('parent')}>
                            Parent Survey
                          </Button>
                        ) : null}
                        {isChildSurveyDue ? (
                          <Button variant='outlined' onClick={() => handleWellbeing('child')}>
                            Child Survey
                          </Button>
                        ) : null}
                      </>
                    }
                  />
                </Grid>
              )}

              {/* --------- Wellness Score ----------------*/}
              {!trackYourProgress && wellbeingScore !== undefined ? (
                <Grid item xs={12}>
                  <DashboardTile
                    icon='images/wellnessdashboard.svg'
                    iconAlt='wellness score'
                    title='Wellness Score'
                    description={
                      <Typography variant='body1'>
                        <strong>{wellbeingScore}</strong> {wellbeingScoreCategory}
                      </Typography>
                    }
                    actions={
                      <Button variant='outlined' onClick={handleWellnessDetails}>
                        Explore Details
                      </Button>
                    }
                  />
                </Grid>
              ) : null}

              {/* --------- i-Wise Score ----------------*/}
              {mcatStatus === 'COMPLETED' && mcatScore !== undefined ? (
                <Grid item xs={12}>
                  <DashboardTile
                    icon='images/mcatIScorewidget.svg'
                    iconAlt='i-wise score'
                    title='i-Wise Score'
                    description={
                      <Typography variant='body1'>
                        <strong>{mcatScore}</strong> {mcatScoreCategory}
                      </Typography>
                    }
                    actions={
                      <Button variant='outlined' onClick={handleMCATDetails}>
                        Explore Details
                      </Button>
                    }
                  />
                </Grid>
              ) : null}

              {/* --------- Treatment Plan ----------------*/}
              {mcatScore !== undefined && wellbeingScore !== undefined ? (
                <Grid item xs={12}>
                  <DashboardTile
                    icon='images/Group 9550.png'
                    iconAlt='treatment plan'
                    title='Treatment Plan'
                    description='Click for active diagnoses & current medications.'
                    actions={
                      <Button variant='outlined' onClick={handleViewDetails}>
                        View Details
                      </Button>
                    }
                  />
                </Grid>
              ) : null}

              {/* --------- What's Next ----------------*/}
              {mcatStatus === 'COMPLETED' && !(hasMoreAppointment || inActiveAppointment) ? (
                <Grid item xs={12}>
                  <DashboardTile
                    icon='images/heart.svg'
                    iconAlt='path to wellness'
                    title="What's Next on Your Path to Wellness"
                    description={
                      <>
                        <Typography variant='h4'>1. Schedule Your New Patient Visit</Typography>
                        <Typography variant='body1'>
                          Book an appointment for a comprehensive evaluation of your child&apos;s emotional, cognitive,
                          and behavioral needs with a board-certified provider. This ensures an accurate diagnosis and
                          a personalized treatment plan.
                        </Typography>
                        <Typography variant='h4'>2. Begin the Treatment Journey</Typography>
                        <Typography variant='body1'>
                          Start your child&apos;s path to well-being with a dedicated provider. Receive timely,
                          convenient care, including medication management when needed.
                        </Typography>
                        <Typography variant='h4'>3. Use Self-Guided Tools</Typography>
                        <Typography variant='body1'>
                          Access practical tools and resources to support your child&apos;s mental and emotional
                          wellness between visits.
                        </Typography>
                        <Typography variant='h4'>4. Track Progress Together</Typography>
                        <Typography variant='body1'>
                          Follow your child&apos;s progress with wellness surveys before each appointment. Combined
                          with provider feedback, these help you see improvements and celebrate milestones.
                        </Typography>
                      </>
                    }
                  />
                </Grid>
              ) : null}
            </Grid>
          </Container>
        </Grid>

        <Grid item xs={12} sx={{ padding: '26px 0' }}>
          {/* <GotQuestions /> */}
        </Grid>
        <Grid item xs={12} sx={{ background: '#F6F9FF', padding: '35px 0' }}>
          <DashboardFooter />
        </Grid>
      </Grid>

      <StyledDialog open={displayModal} fullWidth={false}>
        <DialogContent>
          <Typography variant='h3' textAlign={'center'}>
            Profile completion
          </Typography>
          <Box m={1}>
            <Typography mt={2}>Kindly fill out your profile details before starting your test.</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button sx={{ marginTop: 4 }} onClick={() => navigate(ROUTE.PROFILE)}>
              Okay
            </Button>
          </Box>
        </DialogContent>
      </StyledDialog>
    </>
  );
};

Dashboard.propTypes = {};

export default Dashboard;
